<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import { Quote } from '@/application/shared/domain/Quote';
import { PlanResponse } from '@/planList/domain/PlanResponse';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { ModalDialog, usePlocState } from '@hfc/ui-core';
import PlanDetails from '@/planList/infrastructure/components/PlanDetails.vue';
import PlanList from '@/planList/infrastructure/components/PlanList.vue';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import FeedbackForm from '@/shared/infrastructure/components/FeedbackForm.vue';
import ThankYou from '@/shared/infrastructure/components/ThankYou.vue';
import { useStickyBox } from '@/shared/infrastructure/composition/useStickyBox';
import { usePlanStore } from '@/planList/infrastructure/composition/usePlanStore';
import { useQuoteStore } from '@/quote/infrastructure/composition/useQuoteStore';

const { planStore, planState } = usePlanStore();
const { quoteStore } = useQuoteStore();

const { handleSfApiError } = useHandleApiError();
const { getStickyBoxCompatiblePlan } = useStickyBox();

const props = defineProps<{
  plan: PlanResponse;
  applicationType: AppTypeDentalOrVision;
  effectiveDate: number;
  horizontal?: boolean;
  showBenefitsLink?: boolean;
  showGiveFeedbackButton?: boolean;
  quoteForm?: Quote;
}>();

interface Emits {
  (e: 'show-modal', value: boolean): void;
}

const emits = defineEmits<Emits>();

const {
  dentalAndVisionApplicationStore,
  primaryApplicant,
  application,
} = useDentalAndVisionApplication(props.applicationType);

const dentalAndVisionApplicationState = usePlocState(dentalAndVisionApplicationStore);

const direction = props.horizontal ? 'horizontal' : 'vertical';
const benefitsModal = ref<boolean>(false);
const showFeedbackContent = ref<boolean>(false);
const changePlanModal = ref<boolean>(false);
const selectViewPlans = ref<PlanResponse>(props.plan);
const showFeedbackThankYou = ref<boolean>(false);

const quote = ref({} as Quote);

const benefitsModalContent = computed(() => changePlanModal.value ? selectViewPlans.value : props.plan);

const getQuoteData = async (): Promise<void> => {
  quote.value = props.quoteForm ? props.quoteForm : await planStore.getQuoteData(props.applicationType);
};

const getPlanList = async (): Promise<void> => {
  const { error } = props.applicationType === APPLICATION_TYPES.DENTAL ? await planStore.getDentalPlanList(quote.value) : await planStore.getVisionPlanList(quote.value);
  if (error) {
    handleSfApiError(error);
  }
};

const setSelectedPlan = async (planData: PlanResponse): Promise<void> => {
  await planStore.updateSelectedPlan(planData, props.applicationType);
  await dentalAndVisionApplicationStore.updateApplication({ plan: planData }, props.applicationType);
  dentalAndVisionApplicationStore.setNewPlanSelected(true);
  closeModal();
};

const plans = computed(() => getStickyBoxCompatiblePlan([{ ...props.plan, allowPlanChange: true }], props.effectiveDate));

const changePlan = async (): Promise<void> => {
  await getQuoteData();
  await getPlanList();
  changePlanModal.value = true;
  emits('show-modal', true);
};

const showDetailsModal = (plan: PlanResponse): void => {
  selectViewPlans.value = plan;
  benefitsModal.value = true;
  emits('show-modal', true);
};

const showBenefitsModal = async (): Promise<void> => {
  benefitsModal.value = true;
  emits('show-modal', true);
};

const showFeedbackModal = (): void => {
  showFeedbackContent.value = true;
  emits('show-modal', true);
};

const closeModal = (): void => {
  benefitsModal.value = false;
  changePlanModal.value = false;
  showFeedbackContent.value = false;
  showFeedbackThankYou.value = false;
  dentalAndVisionApplicationStore.setShowReselectPlanModal(false);
  emits('show-modal', false);
};

const closeBenefitsModal = (): void => {
  if (changePlanModal.value) {
    benefitsModal.value = false;

    return;
  }

  closeModal();
};

const feedbackSubmitSuccess = async (): Promise<void> => {
  showFeedbackThankYou.value = true;
};

const triggerChangePlan = async (): Promise<void> => {
  nextTick(async () => {
    if (primaryApplicant.value.main_address?.county && primaryApplicant.value.main_address?.zip_code) {
      await quoteStore.updateQuote({ zip_code: primaryApplicant.value.main_address?.zip_code, county: primaryApplicant.value.main_address?.county }, props.applicationType);
      await quoteStore.submitQuote(props.applicationType);
      changePlan();
    }
  });
};

watch(
  () => dentalAndVisionApplicationState.value.showReselectPlanModal,
  shouldShowReselectPlanModal => {
    if (shouldShowReselectPlanModal) {
      triggerChangePlan();
    }
  }
);
</script>

<template>
  <div class="flex flex-col items-center">
    <sticky-box :change-plan-loading="planState.loadingPlans" v-if="plan.rate" class="z-0" :plans="plans" :force-price-label="true" :direction="direction" :show-total="true" :show-benefits-link="showBenefitsLink" @change-plan="changePlan" @view-benefits="showBenefitsModal"></sticky-box>
    <span v-if="showGiveFeedbackButton" class="hover:cursor-pointer underline text-sm text-blue-600 mt-2" @click="showFeedbackModal">Give feedback</span>
    <ModalDialog v-if="showFeedbackContent" title="Send Us Your Feedback or Request Support" :show-confirm-button="false" @close="closeModal" @confirm="closeModal" :show-footer="false">
      <template #content>
        <FeedbackForm v-if="!showFeedbackThankYou" :application-type="applicationType" :uuid="application.uuid" :show-title="false" :show-shadow="false" @feedback-success="feedbackSubmitSuccess" />
        <ThankYou v-if="showFeedbackThankYou" :show-title="false" :show-shadow="false" />
      </template>
    </ModalDialog>
    <ModalDialog v-if="changePlanModal" title="Reselect Plan" :show-confirm-button="false" size="xl" @close="closeModal" @confirm="closeModal">
      <template #content>
        <div>
          <p v-if="!dentalAndVisionApplicationState.newPlanSelected">You changed your zip code. This will affect your quote and you will have to select an updated plan and price.</p>
          <PlanList
            :application-type="applicationType"
            :plan-list="planStore.state.plans"
            :loading-plans="planState.loadingPlans"
            :applicants="quote.applicants"
            :view-details-button="true"
            :compact="true"
            @selectPlan="setSelectedPlan"
            @viewDetails="showDetailsModal" />
        </div>
      </template>
    </ModalDialog>
    <ModalDialog v-if="benefitsModal" :title="`${plan.name} details`" :show-confirm-button="false" size="xl" @close="closeBenefitsModal" @confirm="closeBenefitsModal">
      <template #content>
        <div class="flex-1 h-full">
          <PlanDetails :plan="benefitsModalContent" :application-type="applicationType" />
        </div>
      </template>
    </ModalDialog>
  </div>
</template>
